/**
 * LabelGenerationPage — `/mail/labels`.
 *
 * Ported from `screens/src/pages/mail/LabelGeneration.jsx`. Lets the clerk
 * queue a label print job by tracking number, choose a template, and view
 * recently rendered labels.
 *
 * Backend wiring (live):
 *   - GET  /v1/printing/jobs?type=LABEL — recent jobs
 *   - POST /v1/printing/jobs            — queue a new label render
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { createPrintJob, listPrintJobs } from '../../lib/api/printing';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const TEMPLATE_OPTIONS = ['label-standard-4x6', 'label-ems-4x6', 'label-registered-4x6'] as const;

export function LabelGenerationPage(): JSX.Element {
  const [tn, setTn] = useState<string>('');
  const [template, setTemplate] = useState<string>(TEMPLATE_OPTIONS[0]);
  const queryClient = useQueryClient();

  const jobs = useQuery({
    queryKey: ['print', 'jobs', 'LABEL'],
    queryFn: () => listPrintJobs({ type: 'LABEL', pageSize: 25 }),
    retry: false,
    staleTime: 15_000,
  });

  const create = useMutation({
    mutationFn: () =>
      createPrintJob({
        type: 'LABEL',
        templateId: template,
        entityId: tn.trim(),
        payload: { trackingNumber: tn.trim().toUpperCase() },
      }),
    onSuccess: () => {
      setTn('');
      void queryClient.invalidateQueries({ queryKey: ['print', 'jobs', 'LABEL'] });
    },
  });

  const denied = jobs.isError && jobs.error instanceof ApiError && (jobs.error.status === 401 || jobs.error.status === 403);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Label Generation"
        description="Queue a label for printing."
      />

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <Printer className="w-4 h-4 text-nipost-green" /> Print a label
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            type="text"
            value={tn}
            onChange={(e) => setTn(e.target.value)}
            placeholder="Tracking number"
            aria-label="Tracking number"
            className="px-3 py-2 text-sm font-mono uppercase border border-border rounded-lg sm:col-span-2"
          />
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            aria-label="Template"
            className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
          >
            {TEMPLATE_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center justify-end gap-3 mt-3">
          {create.isError ? (
            <span className="text-xs text-red-700">{(create.error as Error).message}</span>
          ) : null}
          {create.isSuccess ? (
            <span className="text-xs text-nipost-green">Queued.</span>
          ) : null}
          <button
            type="button"
            disabled={tn.trim().length === 0 || create.isPending}
            onClick={() => create.mutate()}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-nipost-green text-white hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {create.isPending ? 'Queueing…' : 'Queue label'}
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Recent label jobs</h3>
        {jobs.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            Token lacks <code className="font-mono">Printing/listJobs</code>.
          </DeferredNotice>
        ) : jobs.isError ? (
          <DeferredNotice title="Service unreachable">
            {(jobs.error as Error).message}
          </DeferredNotice>
        ) : (jobs.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">No label jobs yet.</p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Job</th>
                  <th className="text-left px-4 py-2">Template</th>
                  <th className="text-left px-4 py-2">Entity</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.data?.items.map((j) => (
                  <tr key={j.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{j.id}</td>
                    <td className="px-4 py-2 text-xs">{j.templateId}</td>
                    <td className="px-4 py-2 text-xs font-mono">{j.entityId ?? '—'}</td>
                    <td className="px-4 py-2"><StatusBadge status={j.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">{new Date(j.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
